import { logMessage, logError, getChatIdentity, isEmpty, hasFeatureFlag } from './helpers'
import { postRequest } from 'src/utils/request-client'
import EventsService from 'src/utils/events-service'

export default class PushService {
  static vapidPublicKey = null

  /**
   * Try to find Notification for various browsers.
   *
   * @returns {Notification|boolean}
   */
  static getNotificationObject() {
    try {
      const getNotification =
        window.Notification || window.mozNotification || window.webkitNotification

      if (getNotification && typeof getNotification === 'function') {
        return getNotification
      }

      return false
    } catch (e) {
      return false
    }
  }

  /**
   * Checks if push notifications are supported in browser and if required config is available.
   *
   * @return {boolean}
   */
  static checkIsSupported() {
    try {
      // VAPID public key
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || null
      if (vapidPublicKey) {
        PushService.vapidPublicKey = vapidPublicKey
      } else {
        logError('[Push] VAPID Public Key not saved in config file', null, 1)
        return false
      }

      if (!PushService.getNotificationObject()) {
        return false
      }

      // Exit if there is no Service Worker
      return navigator && 'serviceWorker' in navigator
    } catch (e) {
      logMessage('[Push] Notifications not supported in this browser', e)
      return false
    }
  }

  /**
   * Checks if push notifications are enabled.
   *
   * @param {string} type Notification type for which to check enabled state
   * @param {object} user Currently logged in user
   * @param {array} featureFlags Feature Flags for current broker
   * @param {boolean} isWidget If it's widget or not
   * @return {boolean}
   */
  static isEnabled(type, user = null, featureFlags = [], isWidget = false) {
    try {
      // All notifications are disabled for widget
      if (isWidget) {
        logMessage('Push disabled for widget')
        return false
      }

      if (type === 'chat') {
        // TODO: PAT-927 Probably hide these console logs later

        // Currently just for logged in users
        if (isEmpty(user)) {
          logMessage('Push disabled if not logged in')
          return false
        }

        // Check if chat push notification feature flag is enabled
        const isPushEnabled = hasFeatureFlag(featureFlags, 'push.chat')

        if (!isPushEnabled) {
          logMessage('Push disabled for this account')
          return false
        }

        return true
      }

      if (type === 'documents') {
        // Check if document push notification feature flag is enabled
        const isPushDocsEnabled = hasFeatureFlag(featureFlags, 'push.documents')

        if (!isPushDocsEnabled) {
          logMessage('Push disabled for this account')
          return false
        }

        // Just for logged in users
        return !isEmpty(user)
      }

      return false
    } catch (e) {
      logMessage('[Push] Notifications not supported in this browser', e)
      return false
    }
  }

  /**
   * Request notification permission (if already allowed it will not popup).
   *
   * Also subscribes current user/visitor (to backend).
   *
   * @param {boolean} isWidget
   * @return {boolean|void}
   */
  static requestPermissionToAllowNotifications(isWidget = false) {
    try {
      if (!PushService.checkIsSupported()) {
        return false
      }

      const getNotification = PushService.getNotificationObject()

      // eslint-disable-next-line no-inner-declarations
      function handleRequestPermission(permission) {
        logMessage('[Push] Notification permission status:', permission)

        // Subscribe to the push service
        PushService.updatePushSubscription(false, isWidget)

        // Listen for changes
        PushService.listenForSubscriptionChange(isWidget)
      }

      // Request permission to receive push notifications
      try {
        // For most browsers
        getNotification.requestPermission().then(function(permission) {
          handleRequestPermission(permission)
        })
      } catch (e) {
        // For Safari which doesn't return a promise for requestPermissions and it throws a TypeError.
        // It takes a callback as the first argument instead.
        // if (e instanceof TypeError) {
        //   getNotification.requestPermission((permission) => {
        //     handleRequestPermission(permission);
        //   });
        // }

        logError('[Push] Request permission', e)
      }
    } catch (e) {
      logError('[Push] Request permission and listen for subscription change', e)
    }
  }

  /**
   * Add listener to know if user declined notifications (from browser's UI).
   *
   * @param {boolean} isWidget
   * @return {boolean|void}
   */
  static listenForSubscriptionChange(isWidget = false) {
    try {
      if (!('permissions' in navigator)) {
        return false
      }

      navigator.permissions.query({ name: 'notifications' }).then(function(notificationPermission) {
        notificationPermission.onchange = function() {
          const permission = notificationPermission.state

          logMessage('[Push] Updated notification permission status:', permission)

          if (permission === 'denied' || permission === 'prompt') {
            // If denied by user (from browser UI) update in database
            PushService.updateSubscriptionOnServer(null, isWidget)
          }
        }
      })
    } catch (e) {
      logError('[Push] Navigator permission', e)
    }
  }

  /**
   * Add or update subscription record in backend (database).
   *
   * Used when:
   * * notifications are allowed with browser's "Allow" popup,
   * * page loads - to sync already allowed notifications which can be lost when Service Worker updates.
   *
   * @param {boolean} isSync Are we just trying to sync existing subscription (e.g. on page load)
   * @param {boolean} isWidget
   * @return {boolean|void}
   */
  static updatePushSubscription(isSync = false, isWidget = false) {
    try {
      if (!PushService.checkIsSupported()) {
        return false
      }

      const getNotification = PushService.getNotificationObject()

      // TODO: PAT-927 Check if this needs to be done only first time or maybe only when changed
      //   UPDATE: Seems like updating frequently is preferred behaviour:
      //   https://developers.google.com/web/updates/2015/03/push-notifications-on-the-open-web#keeping_the_subscription_up_to_date

      // TODO: PAT-927 Re-subscribe after releasing an update and Service Worker re-installs,
      //   maybe in that onUpdate function

      // Exit if already denied
      // if(getNotification.permission === 'denied') {
      //   // If denied but was subscribed, update in database
      //   if(PushService.subscription) {
      //     PushService.updateSubscriptionOnServer(null);
      //   }
      //   return false;
      // }

      // When Service Worker is loaded
      navigator.serviceWorker.ready.then(function(serviceWorkerRegistration) {
        const isAllowed = getNotification.permission === 'granted'

        // Do not ask to "Allow" push subscription if we're just trying to sync existing subscription
        if (isSync && !isAllowed) {
          return false
        }

        try {
          // This syntax might fail in unsupported browsers (e.g. Safari), so we'll catch it
          serviceWorkerRegistration.pushManager
            .subscribe({
              userVisibleOnly: true,
              applicationServerKey: PushService.urlB64ToUint8Array(PushService.vapidPublicKey),
            })
            .then(subscription => {
              // Update in backend (database)
              PushService.updateSubscriptionOnServer(subscription, isWidget)
            })
            .catch(subscribeError => {
              if (getNotification.permission === 'denied') {
                // If denied but was subscribed, update in database
                PushService.updateSubscriptionOnServer(null, isWidget)
              } else {
                logError('[Push] Caught serviceWorkerRegistration.pushManager.subscribe', {
                  permission: getNotification.permission,
                  error: subscribeError,
                })
              }
            })
        } catch (compatibilityError) {
          if (compatibilityError instanceof TypeError) {
            // Try again for Safari
            try {
              // TODO: PAT-927 Subscribe for Safari
              //   https://developer.apple.com/library/archive/documentation/NetworkingInternet/Conceptual/NotificationProgrammingGuideForWebsites/PushNotifications/PushNotifications.html#//apple_ref/doc/uid/TP40013225-CH3-SW1
              //   https://developer.apple.com/notifications/safari-push-notifications/
            } catch (typeError) {
              logError('[Push] TypeError serviceWorkerRegistration.pushManager.subscribe', {
                permission: getNotification.permission,
                error: typeError,
              })
            }
          } else {
            logError('[Push] Subscribe permission', {
              permission: getNotification.permission,
              error: compatibilityError,
            })
          }
        }
      })
    } catch (e) {
      logError('[Push] Update push subscription', e)
    }
  }

  /**
   * Unsubscribe this device from notifications and update subscription record in backend (database).
   *
   * Can be called on some action in our UI.
   *
   * Currently not used.
   *
   * @param {boolean} isWidget
   * @return {boolean|void}
   */
  static unsubscribeUser(isWidget = false) {
    try {
      if (!PushService.checkIsSupported()) {
        return false
      }

      navigator.serviceWorker.ready.then(function(serviceWorkerRegistration) {
        serviceWorkerRegistration.pushManager
          .getSubscription()
          .then(subscription => {
            if (subscription) {
              return subscription.unsubscribe()
            }
          })
          .catch(e => {
            logError('[Push] Error with unsubscribe', e)
          })
          .then(() => {
            // Update to database
            PushService.updateSubscriptionOnServer(null, isWidget)
          })
      })
    } catch (e) {
      logError('[Push] Unsubscribe error', e)
    }
  }

  /**
   * Set badge of standalone App.
   *
   * @param {number|mixed} value It's currently just a number (counter), but emojis will be supported soon
   * @return {boolean}
   */
  static setAppBadge(value = null) {
    if (!value) {
      return false
    }

    try {
      // Set the app badge, for app icons and links. This has a global and
      // semi-permanent effect, outliving the current document.
      if (navigator.setAppBadge) {
        navigator.setAppBadge(value)
      }

      if (window.ExperimentalBadge) {
        window.ExperimentalBadge.set(value)
      }

      // Set the document badge, for the current tab / window icon.
      if (navigator.setClientBadge) {
        navigator.setClientBadge(value)
      } else {
        // TODO: PAT-1890 Update favicon
        // Fall back to setting favicon (or page title).
        // (This is a user-supplied function, not part of the Badge API.)
        // showBadgeOnFavicon(value);
      }

      return true
    } catch (e) {
      logError('[Push] Set app/client badge', e)
    }
  }

  /**
   * Clear badge of standalone App.
   *
   * @return {boolean}
   */
  static clearAppBadge() {
    try {
      if (navigator.clearAppBadge) {
        navigator.clearAppBadge()
      }

      if (window.ExperimentalBadge) {
        window.ExperimentalBadge.clear()
      }

      if (navigator.clearClientBadge) {
        navigator.clearClientBadge()
      } else {
        // TODO: PAT-1890 Update favicon
        // clearBadgeOnFavicon();
      }

      return true
    } catch (e) {
      logError('[Push] Clear app/client badge', e)
      return false
    }
  }

  /**
   *
   * Add or update subscription record in backend (database).
   *
   * Returns
   *
   * @param {PushSubscription|null} subscription PushSubscription object or null if we need to unsubscribe
   * @param {boolean} isWidget
   * @return {Promise<integer|null>}
   */
  static async updateSubscriptionOnServer(subscription = null, isWidget = false) {
    const visitorData = await EventsService.getVisitorData({ isWidget })

    return postRequest(
      {
        base: '/push/updateKioskPushSubscription',
        mock: false,
      },
      {
        subscription: subscription,
        visitorData: visitorData,
        chatIdentity: getChatIdentity(),
      },
    )
      .then(response => {
        try {
          if (response && response.data && response.data.id && response.data.id.length > 0) {
            return response.data.id
          }
        } catch (e) {
          logError('[Push] Get Subscription ID', e, 0)
        }
        return null
      })
      .catch(function(e) {
        logError('[Push] Update subscription', e)
        return null
      })
  }

  /**
   * Convert Base64 string to array, for converting "VAPID public key" to "SW push manager application server key".
   *
   * @param base64String
   * @return {Uint8Array}
   */
  static urlB64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding)
      // eslint-disable-next-line
      .replace(/\-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}
